import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  MessageSquare,
  Tag,
  MapPin,
  CalendarSearch,
  Bookmark,
  ShoppingCart,
  Car,
  Calendar,
  Gauge,
  Info,
  ListTree,
  Bot
} from "lucide-react";
import PartChatDialog from "@/components/PartChatDialog";
import { removeSavedPart, savePart } from "../store/parts/partsAction";
import {
  storeAiSearchData,
  clearAiSearchData
} from "../store/parts/partsSlice";
import {
  fetchMessages,
  fetchSellerById,
  updateIsRead
} from "../store/partChat/partChatActions";
import PartImageSlider from "../hooks/partImageSlider";
import PartPaymentModal from "../components/Modal/PartPaymentModal";
import { openAuthDialog } from "../store/auth/authSlice";
import { toast } from "../components/ui/use-toast";

const AiSearchResultsPage = ({ onOpenChange: controlledOnOpenChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const setIsOpen = controlledOnOpenChange || setInternalIsOpen;
  const location = useLocation();

  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const {
    saveLoading,
    savedParts = [],
    removeLoading,
    aiSearchData
  } = useSelector((state) => state.parts);

  const {
    messages: chatMessages,
    isMessagesLoading,
    isSenderLoading
  } = useSelector((state) => state.partChat);

  // Get data from location.state or Redux store
  const locationStateData = location.state || {};
  const activeSearchData =
    locationStateData.query || locationStateData.results
      ? locationStateData
      : aiSearchData;

  const {
    query,
    results: initialResults,
    aiResponse: initialAiResponse,
    vehicleData
  } = activeSearchData || {};

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [partDetail, setPartDetail] = useState();
  const [selectedConversation, setSelectedConversation] = useState(null);

  // Store search data in Redux when component mounts if we have location.state
  useEffect(() => {
    if (locationStateData.query || locationStateData.results) {
      dispatch(storeAiSearchData(locationStateData));
    }
  }, []);

  const tierStyles = {
    featured: {
      icon: Tag,
      label: "Featured",
      bgColor: "bg-yellow-100 dark:bg-yellow-100",
      textColor: "text-yellow-700 dark:text-yellow-800",
      borderColor: "border-yellow-300 dark:border-yellow-800"
    }
  };

  const getCurrentTier = (part) => {
    if (part?.isFeatured) return tierStyles.featured;
    return null;
  };

  const handleSaveClick = (result, isSaved) => {
    if (!isAuthenticated || !user) {
      // Store current search data before opening auth dialog
      if (locationStateData.query || locationStateData.results) {
        dispatch(storeAiSearchData(locationStateData));
      }

      toast({
        title: "Login Required",
        description: "Please login to save parts.",
        variant: "destructive"
      });
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname
        })
      );
      return;
    }

    const partData = result?.partInfo || result;

    if (isSaved) {
      dispatch(removeSavedPart({ userId: user?.uid, partId: partData?.id }));
    } else {
      dispatch(savePart({ userId: user?.uid, part: partData }));
    }
  };

  const handleChatClick = async (result) => {
    if (!isAuthenticated || !user) {
      // Store current search data before opening auth dialog
      if (locationStateData.query || locationStateData.results) {
        dispatch(storeAiSearchData(locationStateData));
      }

      toast({
        title: "Login Required",
        description: "Please login to send a message to Seller.",
        variant: "destructive"
      });
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
          onSuccessfulAuth: () => setIsOpen(true)
        })
      );
      return;
    }

    try {
      const partData = result?.partInfo || result;
      const sellerData = result?.sellerInfo;

      const seller =
        sellerData ||
        (await dispatch(
          fetchSellerById({ userId: partData?.sellerId })
        ).unwrap());

      const conversation = {
        partInfo: partData,
        otherUserDetail: seller
      };

      dispatch(
        fetchMessages({
          buyerId: user?.uid,
          sellerId: partData?.sellerId,
          partId: partData?.id,
          currentUserId: user?.uid
        })
      );

      setTimeout(() => {
        dispatch(
          updateIsRead({
            currentUserId: user?.uid,
            receiverId: partData?.sellerId,
            partId: partData?.id
          })
        );
      }, 1000);

      setSelectedConversation(conversation);
    } catch (error) {
      console.error("Error fetching seller:", error);
    }
  };

  const handleBuyClick = (part) => {
    if (!isAuthenticated || !user) {
      // Store current search data before opening auth dialog
      if (locationStateData.query || locationStateData.results) {
        dispatch(storeAiSearchData(locationStateData));
      }

      toast({
        title: "Login Required",
        description: "Please login to make a purchase.",
        variant: "destructive"
      });
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname
        })
      );
      return;
    }

    if (!part?.stripePaymentLink) {
      toast({
        title: "Payment Unavailable",
        description: "This seller has not added a Stripe payment link.",
        variant: "destructive"
      });
      return;
    }
    setPartDetail(part);
    setShowPaymentModal(true);
  };

  const getPartImages = (result) => {
    const partData = result?.partInfo || result;

    if (partData?.imageUrls?.length) return partData.imageUrls;
    if (partData?.imageReferences?.length) return partData.imageReferences;
    if (partData?.imageUrl) return [partData.imageUrl];
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  const results = initialResults || [];

  if (!activeSearchData || (!query && !results?.length && !initialAiResponse)) {
    return (
      <motion.div
        className="w-full"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div variants={itemVariants} className="mb-6">
          <div className="flex justify-between">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Search Results
            </h1>
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="flex justify-center items-center min-h-[60vh]" // <-- added
        >
          <Card className="p-10 max-w-xl text-center border-dashed border-2 border-muted-foreground/30 bg-muted/20 shadow-none">
            <CardContent className="flex flex-col items-center gap-4">
              <Bot className="h-12 w-12 text-muted-foreground opacity-70" />
              <h3 className="text-lg font-semibold text-foreground">
                No search data available
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Please perform a new search to see results.
              </p>
              <Button
                variant="default"
                onClick={() => navigate("/")}
                className="mt-4"
              >
                Go to Home
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="w-full"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div variants={itemVariants} className="mb-6">
        <div className="flex justify-between">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Search Results
          </h1>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
        </div>
      </motion.div>
      {results?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="mt-8 mb-8 w-full"
        >
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border border-primary/20 bg-gradient-to-r from-primary/5 via-background to-primary/5 px-6 py-5 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 backdrop-blur-sm">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/10 to-transparent opacity-30 blur-xl" />
            <div className="flex items-start sm:items-center gap-4 relative z-10">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-primary/30 blur-md animate-pulse" />
                <div className="relative z-10 p-3 bg-primary/15 rounded-full flex items-center justify-center shadow-inner">
                  <Info className="h-5 w-5 text-primary" />
                </div>
              </div>

              <div className="flex flex-col">
                <h3 className="text-base sm:text-lg font-semibold text-foreground">
                  {query
                    ? `Results for "${query}"`
                    : "Chat with Kato to find the perfect parts for your vehicle"}
                </h3>

                <p className="mt-1 text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Showing{" "}
                  <span className="text-primary font-semibold text-base">
                    {results?.length}
                  </span>{" "}
                  compatible part{results.length !== 1 ? "s" : ""} for your
                  vehicle.
                </p>

                {/* Vehicle details (conditional) */}
                {vehicleData && vehicleData.length > 0 && (
                  <p className="mt-1 text-xs sm:text-sm text-muted-foreground italic">
                    Vehicle detected:{" "}
                    <span className="text-primary font-medium not-italic">
                      {vehicleData
                        .filter(
                          (item) =>
                            ["Make", "Model Year"].includes(item.Variable) &&
                            item.Value
                        )
                        .map((item) => item.Value)
                        .join(" ")}
                    </span>
                  </p>
                )}
              </div>
            </div>

            {/* Right: Results badge */}
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                <span className="text-sm font-medium text-primary">
                  {results?.length} Result{results.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {vehicleData && (
        <motion.div variants={itemVariants} className="mb-8">
          <Card className="overflow-hidden border border-primary/20 shadow-xl bg-gradient-to-br from-primary/5 to-background/90 backdrop-blur-sm">
            {/* Header */}
            <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5 border-b border-primary/20">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary/10 rounded-full">
                  <Car className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold text-foreground">
                    Vehicle Information
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Matched parts and specifications
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-6 pb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {vehicleData?.map((vehicle, index) => {
                  const isManufacturer =
                    vehicle.Variable?.toLowerCase() === "manufacturer name";

                  return (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 bg-muted/50 rounded-xl border border-border/50 hover:border-primary/50 hover:bg-muted/70 transition-all duration-200"
                    >
                      <div className="flex-shrink-0 p-2 bg-primary/10 rounded-lg flex items-center justify-center">
                        <ListTree className="h-5 w-5 text-primary" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                          {vehicle.Variable}
                        </p>

                        <p
                          className={`text-sm font-semibold text-foreground break-words leading-snug ${
                            isManufacturer ? "line-clamp-2" : ""
                          }`}
                          style={{
                            wordBreak: "break-word",
                            whiteSpace: "normal"
                          }}
                          title={vehicle.Value}
                        >
                          {vehicle.Value || "N/A"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {results.length === 0 ? (
        <motion.div variants={itemVariants}>
          <Card className="p-10 text-center border-dashed border-2 border-muted-foreground/30 bg-muted/20 shadow-none">
            <CardContent className="flex flex-col items-center gap-4">
              <Bot className="h-12 w-12 text-muted-foreground opacity-70" />
              <h3 className="text-lg font-semibold text-foreground">
                No results found
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                {initialAiResponse ||
                  "We couldn't find any parts matching your search criteria."}
              </p>
              {!initialAiResponse && (
                <div className="mt-3 text-sm bg-accent/10 px-4 py-2 rounded-lg text-accent">
                  💡 Tip: You can search by part name, model, or even describe
                  the issue.
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {results.map((result, index) => {
            const partData = result?.partInfo || result;
            const sellerInfo = result?.sellerInfo;
            const tier = getCurrentTier(partData);
            const isSaved = savedParts.some(
              (saved) => saved?.partId === result?.objectID
            );

            return (
              <motion.div key={partData?.id || index} variants={itemVariants}>
                <Card
                  onClick={() => navigate(`/partDetail/${partData?.id}`)}
                  className="group h-full flex flex-col overflow-hidden shadow-lg hover:shadow-primary/20 hover:border-primary transition-shadow duration-300 cursor-pointer"
                >
                  <CardHeader>
                    <div className="aspect-video bg-muted rounded-md mb-1 flex items-center justify-center relative overflow-hidden">
                      <PartImageSlider
                        images={getPartImages(result)}
                        alt={`Image of ${partData?.name}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                      {tier && (
                        <span
                          className={`absolute top-1.5 right-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${tier.bgColor} ${tier.textColor} ${tier.borderColor}`}
                        >
                          <tier.icon className="mr-1 h-2.5 w-2.5" />
                          {tier.label}
                        </span>
                      )}
                      {partData?.isFounderMember && (
                        <span
                          className={`absolute top-1.5 left-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] text-white font-semibold border border-yellow-100 bg-yellow-600`}
                        >
                          Founding Seller
                        </span>
                      )}
                    </div>
                    <div className="flex justify-between items-center group-hover:text-primary">
                      <CardTitle className="text-lg">
                        {partData?.make == "other"
                          ? partData?.sub_make
                          : partData?.make}{" "}
                        {partData?.model == "other"
                          ? partData?.sub_model
                          : partData?.model}{" "}
                        {partData?.year}{" "}
                        {partData?.name == "other"
                          ? partData?.sub_name
                          : partData?.name || "Unknown Part"}
                      </CardTitle>
                      {user?.role === "buyer" && isAuthenticated && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveClick(result, isSaved);
                          }}
                          disabled={saveLoading || removeLoading}
                          className="transition-transform hover:scale-110"
                        >
                          {isSaved ? (
                            <Bookmark className="w-6 h-6 text-primary fill-primary drop-shadow-md" />
                          ) : (
                            <Bookmark className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors duration-300" />
                          )}
                        </button>
                      )}
                    </div>
                    <CardDescription>
                      {partData?.description ||
                        `High quality used ${
                          partData?.name_lowercase || "part"
                        }.`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow space-y-2 text-sm">
                    <div className="flex items-center text-muted-foreground">
                      <Tag className="mr-2 h-4 w-4 text-primary" />
                      Price:{" "}
                      <span className="font-semibold text-foreground ml-1">
                        ${partData?.price?.toFixed(2) || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center text-muted-foreground">
                      <MapPin className="mr-2 h-4 w-4 text-primary" />
                      Seller:{" "}
                      <span className="font-semibold text-foreground ml-1">
                        {partData?.sellerName ||
                          sellerInfo?.companyName ||
                          sellerInfo?.name ||
                          "Unknown Seller"}
                      </span>
                    </div>
                    {/* <div className="flex items-center text-muted-foreground">
                      <CalendarSearch className="mr-2 h-4 w-4 text-primary" />
                      Year:{" "}
                      <span className="font-semibold text-foreground ml-1">
                        {partData?.year || "N/A"}
                      </span>
                    </div> */}
                    {/* {(partData?.make || partData?.model) && (
                      <div className="text-xs text-muted-foreground pt-1">
                        {partData?.make == "other"
                          ? partData?.sub_make
                          : partData?.make}{" "}
                        {partData?.model == "other"
                          ? partData?.sub_model
                          : partData?.model}
                      </div>
                    )} */}
                    <div className="text-xs text-muted-foreground pt-1">
                      {partData?.condition || "Condition: Used - Good"} |
                      {partData?.quantity
                        ? ` Qty: ${partData.quantity}`
                        : " In Stock"}
                    </div>
                  </CardContent>
                  {user?.role === "seller" &&
                  user?.uid === sellerInfo?.id ? null : (
                    <CardFooter className="gap-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChatClick(result);
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full group"
                      >
                        <MessageSquare className="mr-2 h-4 w-4 group-hover:animate-pulse" />
                        Chat
                      </Button>
                      {user &&
                        partData?.stripePaymentLink &&
                        partData?.stripePaymentLink !== "" &&
                        user?.role !== "seller" && (
                          <Button
                            variant="default"
                            size="sm"
                            className="w-full group bg-primary text-white hover:bg-primary/70"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBuyClick(partData);
                            }}
                          >
                            <ShoppingCart className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform" />
                            Buy Now
                          </Button>
                        )}
                    </CardFooter>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
      {selectedConversation && (
        <PartChatDialog
          messages={chatMessages}
          conversation={selectedConversation}
          isOpen={!!selectedConversation}
          isMessagesLoading={isMessagesLoading}
          isSenderLoading={isSenderLoading}
          onOpenChange={(open) => {
            if (!open) setSelectedConversation(null);
          }}
        >
          <></>
        </PartChatDialog>
      )}
      {showPaymentModal && (
        <PartPaymentModal
          open={showPaymentModal}
          part={partDetail}
          close={(isOpen) => {
            if (!isOpen) {
              setShowPaymentModal(false);
              setPartDetail(null);
            }
          }}
        />
      )}
    </motion.div>
  );
};

export default AiSearchResultsPage;
